import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Platform } from 'react-native';
import type { PrescriptionEventDto } from '../../storage/WellCheckStorage';
import DatePickerModal from '../DatePickerModal';

const FREQ_OPTIONS = ['Daily', 'Twice Daily', 'Three Times Daily', 'Four Times Daily', 'As Needed', 'Weekly', 'Monthly'];

const STATUS_OPTIONS = ['Active', 'Refill Needed', 'On Hold', 'Completed'] as const;
type PrescriptionStatus = typeof STATUS_OPTIONS[number];

const STATUS_COLORS: Record<PrescriptionStatus, { bg: string; text: string; border: string }> = {
  'Active':        { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  'Refill Needed': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  'On Hold':       { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
  'Completed':     { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
};

const EMPTY: PrescriptionEventDto = {
  id: '',
  name: '',
  date: '',
  freq: '',
  qty: '',
  totalDoses: '',
  refills: '',
  Track: 'Track',
  status: 'Active',
  trackAction: true,
};

const makePrescriptionId = () => `rx_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

const normalisePrescriptionEvent = (event: PrescriptionEventDto): PrescriptionEventDto => ({
  ...event,
  id: (event.id || '').trim() || makePrescriptionId(),
  qty: (event.qty ?? event.totalDoses ?? '').trim(),
  totalDoses: (event.qty ?? event.totalDoses ?? '').trim(),
  Track: ((event.Track ?? event.track ?? '').trim() !== '')
    ? (event.Track || event.track || 'Track')
    : (event.trackAction === false ? '' : 'Track'),
  trackAction: ((event.Track ?? event.track ?? '').trim() !== '') ? true : event.trackAction !== false,
});

interface Props {
  events: PrescriptionEventDto[];
  onUpdate: (events: PrescriptionEventDto[]) => void;
}

export interface PrescriptionListEditorHandle {
  openAdd: () => void;
}

export const PrescriptionListEditor = forwardRef<PrescriptionListEditorHandle, Props>(
  ({ events, onUpdate }, ref) => {
  const [formVisible, setFormVisible] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<PrescriptionEventDto>(EMPTY);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({ openAdd }));

  const openAdd = () => {
    setDraft({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
    setEditingIdx(null);
    setFormVisible(true);
  };

  const openEdit = (idx: number) => {
    setDraft({ ...normalisePrescriptionEvent(events[idx]) });
    setEditingIdx(idx);
    setFormVisible(true);
  };

  const cancel = () => {
    setFormVisible(false);
    setEditingIdx(null);
    setDatePickerVisible(false);
  };

  const formatDateDisplay = (value: string): string => {
    if (!value) return 'Select date';
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return value;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openDatePicker = () => setDatePickerVisible(true);

  const save = () => {
    if (!draft.name.trim()) return;
    const next = [...events];
    if (editingIdx !== null) next[editingIdx] = normalisePrescriptionEvent(draft);
    else next.push(normalisePrescriptionEvent(draft));
    onUpdate(next);
    setFormVisible(false);
    setEditingIdx(null);
  };

  const remove = (idx: number) => {
    onUpdate(events.filter((_, i) => i !== idx));
    setRemoveConfirmIdx(null);
  };

  const toggleTrackAction = (idx: number) => {
    const next = [...events];
    const current = normalisePrescriptionEvent(next[idx]);
    const nextTracked = !(current.trackAction !== false);
    next[idx] = {
      ...current,
      trackAction: nextTracked,
      Track: nextTracked ? (current.Track || current.track || 'Track') : '',
    };
    onUpdate(next);
  };

  // Determine if the current freq value is a custom (non-chip) value
  const isCustomFreq = draft.freq !== '' && !FREQ_OPTIONS.includes(draft.freq);

  const renderForm = (
    isEdit: boolean,
    inlineInCard: boolean = false,
  ) => {
    return (
    <View style={[styles.form, inlineInCard && styles.inlineForm]}>
      <Text style={styles.formTitle}>
        {isEdit ? '✏️ Edit Prescription' : '➕ Add Prescription'}
      </Text>

      {/* Medication name */}
      <Text style={styles.fieldLabel}>Medication Name *</Text>
      <TextInput
        style={styles.input}
        value={draft.name}
        onChangeText={v => setDraft(d => ({ ...d, name: v }))}
        placeholder="e.g. Amoxicillin 250mg"
        placeholderTextColor="#aaa"
      />

      {/* Date prescribed */}
      <Text style={styles.fieldLabel}>Date Prescribed</Text>
      {Platform.OS === 'web' ? (
        // @ts-ignore — web-only input
        <input
          type="date"
          value={draft.date}
          onChange={(e: any) => setDraft(d => ({ ...d, date: e.target.value }))}
          style={{
            width: 160, border: '1px solid #d0d7de', borderRadius: 6,
            padding: '6px 10px', fontSize: 13, color: '#2c3e50',
            backgroundColor: '#fafbfc', outline: 'none', marginBottom: 8,
          }}
        />
      ) : (
        <TouchableOpacity style={styles.dateButton} onPress={openDatePicker} activeOpacity={0.75}>
          <Text style={styles.dateButtonText}>{formatDateDisplay(draft.date)}</Text>
        </TouchableOpacity>
      )}

      {/* Frequency chips */}
      <Text style={styles.fieldLabel}>Frequency</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {FREQ_OPTIONS.map(f => {
          const sel = draft.freq === f;
          return (
            <TouchableOpacity
              key={f}
              style={{
                paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, borderWidth: 1,
                borderColor: sel ? '#10b981' : '#d0d7de',
                backgroundColor: sel ? '#10b981' : '#fafbfc',
              }}
              onPress={() => setDraft(d => ({ ...d, freq: sel ? '' : f }))}
            >
              <Text style={{ fontSize: 12, color: sel ? '#fff' : '#5a6472', fontWeight: sel ? '600' : '400' }}>
                {f}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {/* Custom frequency text input */}
      <TextInput
        style={[styles.input, { marginBottom: 8 }]}
        value={isCustomFreq ? draft.freq : ''}
        onChangeText={v => setDraft(d => ({ ...d, freq: v }))}
        placeholder="or type custom frequency..."
        placeholderTextColor="#aaa"
      />

      {/* Total Doses and Refills side by side */}
      <View style={{ flexDirection: 'row', gap: 12 }}>
        <View style={{ flex: 1 }}>
          <Text style={styles.fieldLabel}>Total Doses</Text>
          <TextInput
            style={styles.input}
            value={draft.totalDoses ?? draft.qty ?? ''}
            onChangeText={v => setDraft(d => ({ ...d, totalDoses: v, qty: v }))}
            placeholder="e.g. 30 tablets"
            placeholderTextColor="#aaa"
          />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.fieldLabel}>Refills</Text>
          <TextInput
            style={styles.input}
            value={draft.refills}
            onChangeText={v => setDraft(d => ({ ...d, refills: v }))}
            placeholder="0"
            placeholderTextColor="#aaa"
            keyboardType="number-pad"
          />
        </View>
      </View>

      {/* Status */}
      <Text style={styles.fieldLabel}>Status</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {STATUS_OPTIONS.map(s => {
          const sel = (draft.status || 'Active') === s;
          const sc = STATUS_COLORS[s];
          return (
            <TouchableOpacity
              key={s}
              style={{
                paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, borderWidth: 1,
                borderColor: sel ? sc.border : '#d0d7de',
                backgroundColor: sel ? sc.bg : '#fafbfc',
              }}
              onPress={() => setDraft(d => ({ ...d, status: s }))}
            >
              <Text style={{ fontSize: 12, color: sel ? sc.text : '#5a6472', fontWeight: sel ? '600' : '400' }}>
                {s}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Save / Cancel buttons */}
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
        <TouchableOpacity style={styles.saveBtn} onPress={save}>
          <Text style={styles.saveBtnText}>Save</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.cancelBtn} onPress={cancel}>
          <Text style={styles.cancelBtnText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
  };

  return (
    <View style={styles.container}>
      <DatePickerModal
        visible={datePickerVisible}
        title="Select Date Prescribed"
        initialValue={draft.date}
        accentColor="#10b981"
        onCancel={() => setDatePickerVisible(false)}
        onConfirm={(dateValue) => {
          setDraft((current) => ({ ...current, date: dateValue }));
          setDatePickerVisible(false);
        }}
      />

      {/* Active & on-hold prescriptions */}
      {(() => {
        const active = events.map((p, idx) => ({ p, idx })).filter(({ p }) => (p.status || 'Active') !== 'Completed');
        const completed = events.map((p, idx) => ({ p, idx })).filter(({ p }) => (p.status || 'Active') === 'Completed');

        const renderRow = (p: PrescriptionEventDto, idx: number, isHistory: boolean) => {
          const status = (p.status || 'Active') as PrescriptionStatus;
          const sc = STATUS_COLORS[status] ?? STATUS_COLORS['Active'];
          return (
            <View
              key={idx}
              style={[
                styles.rxRow,
                isHistory && { opacity: 0.75, backgroundColor: '#f9fafb' },
                formVisible && editingIdx === idx && styles.rxRowEditing,
              ]}
            >
              <View style={styles.rxRowTop}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                    <Text style={[styles.rxName, isHistory && { color: '#6b7280' }]}>{p.name}</Text>
                    {status !== 'Active' && (
                      <View style={{ backgroundColor: sc.bg, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 1 }}>
                        <Text style={{ fontSize: 10, color: sc.text, fontWeight: '600' }}>{status}</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.rxDetail}>
                    {[
                      p.freq,
                      (p.qty ?? p.totalDoses) ? `Doses: ${p.qty ?? p.totalDoses}` : null,
                      p.refills    ? `Refills: ${p.refills}` : null,
                      p.date       ? `Started: ${p.date}` : null,
                    ].filter(Boolean).join('  ·  ')}
                  </Text>
                </View>
                <View style={styles.rxControlsColumn}>
                  <TouchableOpacity onPress={() => toggleTrackAction(idx)} style={styles.trackActionToggle}>
                    <Text style={[styles.trackActionToggleText, p.trackAction === false && styles.trackActionToggleTextOff]}>
                      {p.trackAction === false ? '☐ Track in Action Plan' : '☑ Track in Action Plan'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => ((formVisible && editingIdx === idx) ? cancel() : openEdit(idx))}
                    style={styles.rxActionBtn}
                  >
                    <Text style={styles.rxActionText}>{formVisible && editingIdx === idx ? 'Cancel' : 'Edit'}</Text>
                  </TouchableOpacity>
                  {!isHistory && (
                    removeConfirmIdx === idx ? (
                      <View style={{ flexDirection: 'row', gap: 4 }}>
                        <TouchableOpacity onPress={() => remove(idx)} style={styles.rxActionBtn}>
                          <Text style={[styles.rxActionText, { color: '#e74c3c', fontWeight: '700' }]}>Confirm?</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setRemoveConfirmIdx(null)} style={styles.rxActionBtn}>
                          <Text style={styles.rxActionText}>Cancel</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TouchableOpacity onPress={() => setRemoveConfirmIdx(idx)} style={styles.rxActionBtn}>
                        <Text style={[styles.rxActionText, { color: '#e74c3c' }]}>Remove</Text>
                      </TouchableOpacity>
                    )
                  )}
                </View>
              </View>
              {formVisible && editingIdx === idx && (
                <View style={styles.inlineEditSection}>
                  {renderForm(true, true)}
                </View>
              )}
            </View>
          );
        };

        return (
          <>
            {active.length === 0 ? (
              <Text style={styles.emptyText}>No active prescriptions on record.</Text>
            ) : (
              active.map(({ p, idx }) => renderRow(p, idx, false))
            )}

            {completed.length > 0 && (
              <>
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 4 }}
                  onPress={() => setShowHistory(v => !v)}
                >
                  <Text style={{ fontSize: 12, color: '#6b7280', fontWeight: '600' }}>
                    {showHistory ? '▾' : '▸'} Completed History ({completed.length})
                  </Text>
                </TouchableOpacity>
                {showHistory && completed.map(({ p, idx }) => renderRow(p, idx, true))}
              </>
            )}
          </>
        );
      })()}

      {/* Add button — hidden while form is open, or always hidden when managed externally */}
      {!formVisible && false && (
        <TouchableOpacity style={styles.addBtn} onPress={openAdd}>
          <Text style={styles.addBtnText}>＋ Add Prescription</Text>
        </TouchableOpacity>
      )}

      {/* Inline add form at card bottom */}
      {formVisible && editingIdx === null && renderForm(false)}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  emptyText: {
    fontSize: 13,
    color: '#95a5a6',
    fontStyle: 'italic',
    marginBottom: 8,
  },
  rxRow: {
    flexDirection: 'column',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e8ecef',
    marginBottom: 6,
  },
  rxRowTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rxName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2c3e50',
  },
  rxDetail: {
    fontSize: 11,
    color: '#7f8c8d',
    marginTop: 2,
  },
  rxControlsColumn: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    minWidth: 190,
  },
  trackActionToggle: {
    marginBottom: 6,
    alignSelf: 'stretch',
  },
  trackActionToggleText: {
    fontSize: 13,
    color: '#065f46',
    fontWeight: '600',
    textAlign: 'right',
  },
  trackActionToggleTextOff: {
    color: '#6b7280',
  },
  rxActionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  rxActionText: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '500',
  },
  addBtn: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#10b981',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  addBtnText: {
    fontSize: 13,
    color: '#10b981',
    fontWeight: '600',
  },
  form: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#f0fdf4',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#6ee7b7',
  },
  inlineEditSection: {
    marginTop: 8,
    paddingTop: 8,
  },
  inlineForm: {
    marginTop: 0,
    backgroundColor: '#f7fdf9',
  },
  rxRowEditing: {
    backgroundColor: '#f0fdf4',
    borderColor: '#6ee7b7',
  },
  formTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#065f46',
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  dateButton: {
    width: 160,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  dateButtonText: {
    fontSize: 13,
    color: '#2c3e50',
    textAlign: 'center',
  },
  saveBtn: {
    flex: 1,
    backgroundColor: '#10b981',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
  },
  saveBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#f6f8fa',
  },
  cancelBtnText: {
    color: '#5a6472',
    fontSize: 13,
    fontWeight: '500',
  },
});
